import * as React from "react";
import { Slot } from "radix-ui";
import { cn } from "@/lib/utils";
import { Spinner } from "./spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "link";
type Size = "md" | "sm" | "icon" | "icon-sm";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-primary text-primary-fg hover:bg-primary-hover border border-primary",
  secondary: "bg-surface text-fg border border-border-strong hover:bg-surface-2",
  ghost: "text-fg hover:bg-row-hover border border-transparent",
  danger: "bg-surface text-danger border border-border-strong hover:bg-danger-soft",
  link: "text-primary hover:underline border border-transparent px-0 h-auto",
};

const SIZES: Record<Size, string> = {
  md: "h-8 px-3 gap-1.5",
  sm: "h-7 px-2 gap-1 text-xs",
  icon: "h-8 w-8 justify-center",
  "icon-sm": "h-6 w-6 justify-center",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "secondary", size = "md", loading, disabled, asChild, children, type = "button", ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex shrink-0 select-none items-center whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-100",
        "disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <>
          <Spinner className="size-3.5" />
          {size === "icon" || size === "icon-sm" ? null : children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
});
